"use client"

import React, { createContext, useContext, useEffect, useState } from "react"
import { Session, User } from "@supabase/supabase-js"
import supabaseClient from "@/utils/supabase-browser"
import { AppSupabaseClient } from "@/types"

interface AuthContextType {
    user: User | null
    session: Session | null
    supabaseClient: AppSupabaseClient
    isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * AuthProvider - Replaces deprecated SessionContextProvider from @supabase/auth-helpers-react
 * Uses @supabase/ssr compatible supabase-browser client
 * 
 * @param initialSession - The initial session from server
 * @param initialUser - Optional user override for demo mode (when no real session exists)
 */
export function AuthProvider({ 
    children, 
    initialSession,
    initialUser 
}: { 
    children: React.ReactNode
    initialSession: Session | null
    initialUser?: User | null
}) {
    const [session, setSession] = useState<Session | null>(initialSession)
    // Use initialUser if provided (demo mode), otherwise derive from session
    const [user, setUser] = useState<User | null>(initialUser ?? initialSession?.user ?? null)
    const [isLoading, setIsLoading] = useState(!initialSession && !initialUser)

    useEffect(() => {
        // Skip auth state sync for demo mode (when initialUser is provided without session)
        if (initialUser && !initialSession) {
            setIsLoading(false)
            return
        }

        // Get initial session if not provided
        if (!initialSession) {
            supabaseClient.auth.getSession().then(({ data: { session } }) => {
                setSession(session)
                setUser(session?.user ?? null)
                setIsLoading(false)
            })
        }

        // Subscribe to auth state changes
        const {
            data: { subscription }
        } = supabaseClient.auth.onAuthStateChange((_event, session) => {
            setSession(session)
            setUser(session?.user ?? null)
            setIsLoading(false)
        })

        return () => {
            subscription.unsubscribe()
        }
    }, [initialSession, initialUser])

    const value: AuthContextType = {
        user,
        session,
        supabaseClient,
        isLoading
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * useAuth - Hook to access auth context
 * Replaces useSession from @supabase/auth-helpers-react
 */
export function useAuth(): AuthContextType {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider")
    }
    return context
}

/**
 * useUser - Hook to access current user
 * Replaces useUser from @supabase/auth-helpers-react
 */
export function useUser(): User | null {
    const { user } = useAuth()
    return user
}

/**
 * useSession - Hook to access current session
 * Replaces useSession from @supabase/auth-helpers-react
 */
export function useSession(): Session | null {
    const { session } = useAuth()
    return session
}

/**
 * useSupabaseClient - Hook to access typed Supabase client
 * Replaces useSupabaseClient from @supabase/auth-helpers-react
 */
export function useSupabaseClient(): AppSupabaseClient {
    const { supabaseClient } = useAuth()
    return supabaseClient
}

/**
 * useAuthSafe - Safe version of useAuth that returns null when outside AuthProvider
 * Use this in components that may render both with and without AuthProvider
 */
export function useAuthSafe(): AuthContextType | null {
    const context = useContext(AuthContext)
    return context ?? null
}

/**
 * useUserSafe - Safe version of useUser that returns null when outside AuthProvider
 * Use this in components that may render both with and without AuthProvider
 */
export function useUserSafe(): User | null {
    const context = useContext(AuthContext)
    return context?.user ?? null
}
